import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../../i18n";
import MIcon from "../../icons/MIcon";

/**
 * LanguageSwitcher — custom drop-up menu for selecting the UI language.
 *
 * A native <select> renders its option list with OS styling and can't open
 * fully inside the cramped sidebar footer, so we use a small popover that
 * opens upward (the switcher sits near the bottom of the sidebar) and lists
 * every supported language with the active one marked.
 *
 * The choice is persisted by i18next's LanguageDetector (localStorage key
 * "language").
 */
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const current = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const activeLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === current) ||
    SUPPORTED_LANGUAGES[0];

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selectLang = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Drop-up menu */}
      {open && (
        <div className="absolute bottom-full left-2 right-2 mb-1.5 py-1 rounded-lg bg-surface-light-lowest dark:bg-surface-container-high border border-surface-light-highest dark:border-white/10 shadow-xl z-50 animate-float-in">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isActive = lang.code === activeLang.code;
            return (
              <button
                key={lang.code}
                onClick={() => selectLang(lang.code)}
                className={`
                  w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                  ${
                    isActive
                      ? "text-brand dark:text-brand font-medium bg-brand/5 dark:bg-brand/10"
                      : "text-on-surface-light-variant dark:text-[#a0a0a0] hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-container dark:hover:bg-[#242424]"
                  }
                `}
              >
                <span className="w-4 flex-shrink-0 flex items-center justify-center">
                  {isActive && <MIcon name="check" className="!text-[16px]" />}
                </span>
                <span className="flex-1 text-left">{lang.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-on-surface-light-variant dark:text-[#a0a0a0] hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-container dark:hover:bg-[#242424] transition-all duration-200"
      >
        <MIcon name="language" />
        <span className="flex-1 text-left">{activeLang.label}</span>
        <MIcon
          name="expand_more"
          className={`!text-[18px] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
    </div>
  );
};

export default LanguageSwitcher;
